import { Box, Button, Text } from '@nerdwallet/currency';
import { getLegacyId } from '@nerdwallet/marketplace/selectors/credit-cards';
import { CompareCardIdListCookieName } from '@nerdwallet/compare-credit-cards-bar/dist/redux/actions/cookie-names';
import { getJSONFromCookie } from '@nerdwallet/compare-credit-cards-bar/dist/helpers/cookie-helpers';
import { useEffect, useMemo } from 'react';
import _ from 'lodash';
import pipe from 'lodash/fp/pipe';
import curryN from 'lodash/fp/curryN';

import AddCardIcon from './add-card-icon';

import styles from './styles.module.less';

// We can get credit card ID or if either it's not a credit card or it doesn't have id
// we use just marketplace entity ID
const getId = (entity) => getLegacyId(entity) || entity.id;

const findCardsAndHandleThem = curryN(2, (ids, array) =>
  array
    .filter((entity) => ids?.includes(getId(entity)))
    .map((entity) => ({
      ...entity,
      uniqueId: entity.uniqueId || entity.id,
    }))
);

const getStructuredContentEntities = (store) =>
  Object.values(store.get())
    // null is object so we need to check if the value is truthy or not
    .filter(({ value }) => !!value && typeof value === 'object')
    .map(({ value }) => Object.values(value)[0])
    .filter(Boolean);

export const useComparisonPanelProps = (
  compareCards,
  saveToCompare,
  removeFromCompare,
  store,
  { active, emptyLabel } = {}
) => {
  useEffect(() => {
    /**
     * We cannot use "addCompareCookie" that the compare-credit-cards-bar library
     * provides. They work only with credit cards API but we have to use at least loans.
     * Here I add data from the structured-content library to the store.
     */
    if (active && typeof window !== 'undefined' && store) {
      // If you update the compare-credit-cards-bar library make sure they don't update
      // the cookie name.
      const cardIDs = _.uniq(
        getJSONFromCookie(CompareCardIdListCookieName, [])
      );

      if (Array.isArray(cardIDs) && cardIDs.length) {
        saveToCompare({
          cards: pipe(
            getStructuredContentEntities,
            findCardsAndHandleThem(cardIDs),
            (array) => array.map((entity) => ({ id: getId(entity) }))
          )(store),
        });
      }
    }
  }, [active, saveToCompare, store]);

  const cards = useMemo(
    () =>
      active && Array.isArray(compareCards?.cardIDsToCompare)
        ? pipe(
            getStructuredContentEntities,
            findCardsAndHandleThem(compareCards?.cardIDsToCompare || [])
          )(store)
        : [],
    [active, compareCards?.cardIDsToCompare, store]
  );

  return useMemo(() => {
    return {
      classes: {
        close: styles.close,
        panel: styles.panel,
      },
      comparisonGridProps: {
        classes: {
          grid: styles.gridWrapper,
          header: styles.header,
          tabLabel: styles.tabLabel,
          tabWrapper: styles.tabWrapper,
          tabContent: styles.tabContent,
          tabContentDesktop: styles.tabContentDesktop,
          tabContentMobile: styles.tabContentMobile,
        },
        compareHeader: {
          gridHeader: 'Compare at-a-glance',
        },
        gridTabs: {
          tabs: [
            {
              label: 'Selected products',
              entitiesToCompare: cards,
              canRemove: true,
              showSelectedEntitiesCount: true,
              emptyState: (close) => (
                <Button
                  onClick={close}
                  tertiary
                  className={styles.addCardButton}
                >
                  <Box
                    component="span"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    className={styles.addCardText}
                  >
                    <AddCardIcon className={styles.addCardIcon} />
                    <Text component="span" size="small" bold>
                      {emptyLabel}
                    </Text>
                  </Box>
                </Button>
              ),
              classes: {
                container: styles.tabContentContainer,
                footer: styles.tabFooter,
                title: styles.tableHeaderCell,
                titleCell: styles.tableHeaderCell,
                applyNow: styles.applyNow,
                titleRow: styles.row,
                headerRow: styles.headerRow,
                headerCell: styles.headerCell,
                image: styles.image,
                table: styles.table,
              },
            },
          ],
        },
        removeEntityClickHandler: (offerId) => {
          const removedCard = cards.find((offer) => offer.id === offerId);

          if (removedCard) {
            removeFromCompare(getId(removedCard));
          }
        },
      },
      comparisonGridSkittleProps: {
        numEntitiesToCompare: cards.length,
        showSkittlePopover: false,
      },
    };
  }, [cards, removeFromCompare, emptyLabel]);
};
